import { BUSINESS, LOGO_URL, SERVICES } from "../../mocks/businessData";
import TrustBar from "../home/components/TrustBar";
import AIAnswerBlock from "../home/components/AIAnswerBlock";
import ProcessSection from "../home/components/ProcessSection";
import WhyChooseUs from "../home/components/WhyChooseUs";
import ServiceAreasSection from "../home/components/ServiceAreasSection";
import TestimonialsSection from "../home/components/TestimonialsSection";
import FAQSection from "../home/components/FAQSection";
import QuoteFormSection from "../home/components/QuoteFormSection";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const HERO_BG = "https://public.readdy.ai/ai/img_res/d02d72dcd6b0ca242a87ccda96172389.jpg";
const CLEANING_1 = "https://static.wixstatic.com/media/916227_c540b24c96884463a9685f9d4cd9da56~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/IMG_20190324_190320_445.jpg";
const CLEANING_2 = "https://static.wixstatic.com/media/916227_3f2e5156be4145c5bb40dd02567444b3~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/IMG_20190415_174425_557_edited.jpg";
const CLEANING_3 = "https://static.wixstatic.com/media/916227_f086a1a54d6640479349ccdff3e24ee3~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/20170730_163852.jpg";
const EXPERT_IMG = "https://static.wixstatic.com/media/916227_366d057f9fa24d878fe40662df046d8e~mv2.jpg/v1/fill/w_340,h_340,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/IMG_20190510_005136_772.jpg";

const STATS = [
  { value: "40+",  label: "Years of Mastery" },
  { value: "340+", label: "Five-Star Reviews" },
  { value: "24/7", label: "Always Available" },
  { value: "4",    label: "States Served" },
];

const CREDENTIALS = [
  "Professional, Courteous & White-Glove Certified Team",
  "Fully Insured — Liability & Workers' Compensation",
  "Bilingual Service Available",
  "Eco-Friendly, Odor-Free, Child & Pet-Safe Solutions",
  "All Major Credit Cards Accepted",
  "Recommended by Hunter Douglas, Graber & Levolor",
  "Trusted by Home Depot, Lowe's & Ethan Allen",
  "Partnered with Interior Designers & Decorators",
  "Emergency 24/7 Availability",
];

export default function AboutPage() {
  const ref = useScrollReveal();
  const ref2 = useScrollReveal();

  return (
    <main>
      {/* 1 — Hero */}
      <section className="relative pt-44 pb-32 overflow-hidden" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/90 via-stone-950/72 to-stone-950/92" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-left">
          <div className="hero-badge flex items-center gap-3 mb-8">
            <span className="inline-block w-10 h-px bg-amber-500" />
            <span className="text-amber-400 text-xs tracking-[0.40em] uppercase font-medium">
              Est. 40+ Years · Belleville, NJ
            </span>
          </div>
          <h1 className="hero-h1 font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] text-white mb-6 leading-[1.03] max-w-4xl">
            New Jersey&apos;s Most Trusted
            <span className="block italic text-amber-300 mt-1">Cleaning Artisans.</span>
          </h1>
          <div className="hero-sub w-20 h-px bg-amber-500 mb-7" />
          <p className="hero-sub text-stone-300 text-base md:text-xl max-w-lg leading-relaxed font-light mb-12">
            Backed by 340+ five-star reviews and four decades of expertise, we set the gold standard
            for window treatment care across NJ, NY, PA &amp; CT.
          </p>
          <div className="hero-ctas flex flex-col sm:flex-row items-start gap-4">
            <a href="tel:19734891019" className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill mr-2" />{BUSINESS.phone}
            </a>
            <a href="#quote-form" onClick={(e) => { e.preventDefault(); document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" }); }} className="btn-luxury border border-white/30 hover:border-amber-400/70 bg-white/5 hover:bg-white/10 text-white hover:text-amber-300 text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer transition-colors duration-300">
              Schedule Complimentary Pickup
            </a>
          </div>
        </div>
      </section>

      {/* 2 — Trust Bar */}
      <TrustBar />

      {/* 3 — Our Story + Credentials */}
      <section className="py-0 bg-white" ref={ref}>
        {/* Stats band */}
        <div className="bg-stone-950 py-14">
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-800">
            {STATS.map((s) => (
              <div key={s.label} className="text-center py-8 px-6 bg-stone-950">
                <p className="font-serif text-4xl md:text-5xl text-amber-400 mb-2">{s.value}</p>
                <p className="text-stone-500 text-xs tracking-[0.3em] uppercase">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20">
          {/* Story + Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="reveal">
              <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">Our Story</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
                Decades of Mastery,
                <span className="block italic text-amber-700 mt-1">Thousands of Renewed Homes</span>
              </h2>
              <div className="gold-rule mb-8" />
              <div className="space-y-5 text-stone-600 text-sm leading-[1.9]">
                <p>
                  The Drapery &amp; Blind Co. has set the standard for quality window treatment cleaning
                  across New Jersey and beyond for over four decades. Founded with immense experience
                  in the cleaning industry, our mission has always been to be the most trusted,
                  most thorough, and most professional cleaning company in the region.
                </p>
                <p>
                  Our trained professionals carefully take down your window treatments, transport them
                  to our professional facility, inspect and clean them on both sides, steam and pin
                  them to perfection, then return and rehang them at your convenience —
                  <strong className="text-stone-800"> exactly the proper way to care for premium window treatments.</strong>
                </p>
                <p>
                  We are completely insured with liability and worker&apos;s comp insurance.
                  Recommended and trusted by Home Depot, Lowe&apos;s, Blinds to Go, Ethan Allen,
                  JC Penney, Hunter Douglas, and Graber — the industry&apos;s most respected names.
                </p>
              </div>
            </div>
            <div className="reveal reveal-delay-2 grid grid-cols-2 gap-3">
              <div className="w-full h-56 overflow-hidden rounded-sm">
                <img src={CLEANING_1} alt="Expert drapery cleaning" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="w-full h-56 overflow-hidden rounded-sm mt-8">
                <img src={CLEANING_2} alt="Blind cleaning service" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="w-full h-56 overflow-hidden rounded-sm -mt-8">
                <img src={CLEANING_3} alt="Professional results" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="w-full h-56 overflow-hidden rounded-sm">
                <img src={EXPERT_IMG} alt="Expert craftsmanship" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </div>

          {/* Credentials + Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" ref={ref2}>
            <div className="reveal bg-stone-950 p-8 rounded-sm">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-700/20 border border-amber-700/30 rounded-sm mb-6">
                <i className="ri-award-fill text-amber-500 text-lg" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-6">Expert Credentials</h3>
              <ul className="space-y-3">
                {CREDENTIALS.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-stone-400 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-amber-600 text-sm" />
                    </span>{c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal reveal-delay-2 bg-stone-50 border border-stone-100 p-8 rounded-sm">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-amber-100 rounded-sm mb-6">
                <i className="ri-service-fill text-amber-700 text-lg" />
              </div>
              <h3 className="font-serif text-2xl text-stone-900 mb-6">Services We Provide</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {SERVICES.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 bg-white border border-stone-100 hover:border-amber-200 px-3 py-3 rounded-sm transition-colors group cursor-default">
                    <div className="w-8 h-8 flex-shrink-0 overflow-hidden rounded-sm">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-stone-700 text-xs font-semibold group-hover:text-amber-800 transition-colors">{s.title} Cleaning</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 — AI Answer Block */}
      <AIAnswerBlock />
      {/* 5 — Process */}
      <ProcessSection />
      {/* 6 — Why Choose Us */}
      <WhyChooseUs />
      {/* 7 — Service Areas */}
      <ServiceAreasSection />
      {/* 8 — Testimonials */}
      <TestimonialsSection />
      {/* 9 — FAQ */}
      <FAQSection />
      {/* 10 — Final CTA + Form */}
      <QuoteFormSection />
    </main>
  );
}
